import type { PianoChart } from '@shared/pianoChart';
import { TILE_CORNER_RADIUS_PX } from './pianoRenderConstants';
import { lightenToward, withAlpha } from './pianoRenderColour';

/**
 * Whether the browser ships a native `CanvasRenderingContext2D.roundRect`
 * implementation. Modern Chrome / Safari 16+ / Firefox 113+ all have
 * it, and the native path is 3-5× faster than the arcTo polyfill on
 * mobile compositors. Detected once at module load — the result is
 * stable across the session.
 */
const HAS_NATIVE_ROUND_RECT =
  typeof CanvasRenderingContext2D !== 'undefined' &&
  typeof (CanvasRenderingContext2D.prototype as { roundRect?: unknown }).roundRect === 'function';

/**
 * Rounded-rect path helper. Routes to the browser's native
 * `roundRect` where available (modern Chromium / Safari 16+ /
 * Firefox 113+) for the 3-5× per-path speedup on phones; falls back
 * to the manual arcTo composition on older browsers so we don't
 * regress visually anywhere the project still loads.
 */
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  if (HAS_NATIVE_ROUND_RECT) {
    ctx.roundRect(x, y, width, height, r);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.arcTo(x + width, y, x + width, y + r, r);
  ctx.lineTo(x + width, y + height - r);
  ctx.arcTo(x + width, y + height, x + width - r, y + height, r);
  ctx.lineTo(x + r, y + height);
  ctx.arcTo(x, y + height, x, y + height - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

/**
 * How much to brighten a hold tile's base colour vs the matching tap
 * tile (mix toward white). Subtle: ~14% takes a saturated palette pad
 * one step lighter without changing the hue, leaving the eye to lock
 * on to the chevron arrows as the primary "this is a hold" cue.
 */
const HOLD_TILE_LIGHTEN_AMOUNT = 0.14;

/**
 * Upward-pointing chevron geometry repeated along a hold ribbon.
 * Reads as "keep your finger down — there's more ribbon coming." Sits
 * over the body so the player has a constant cue that the visual
 * object in the lane is a hold, not a stretched tap.
 */
const HOLD_CHEVRON_HEIGHT_PX = 9;
const HOLD_CHEVRON_SPACING_PX = 30;
const HOLD_CHEVRON_THICKNESS_PX = 2.4;
/** Width as a fraction of the lane width — keeps arrows in proportion on tablet vs phone. */
const HOLD_CHEVRON_WIDTH_FRAC = 0.42;
/** Below this body height the chevron stream is suppressed (a single arrow on a stub ribbon reads as clutter). */
const HOLD_CHEVRON_MIN_BODY_PX = 36;
/**
 * While the hold is engaged the chevrons scroll continuously upward
 * at this rate (CSS pixels per second). Tuned slow enough not to be
 * distracting but fast enough that the player perceives the arrows
 * as actively flowing — "your energy is rising," a constant reward
 * for keeping the key down. Static (no scroll) before engagement.
 */
const HOLD_CHEVRON_SCROLL_PX_PER_SEC = 42;

/**
 * Single-tile shape primitives — taps, holds, and the chord-pair tie
 * mark. Pulled out of the iteration layer so the iterator
 * (`pianoDrawTiles.ts`) reads top-to-bottom as "decide what each
 * lane event becomes" and the painting math lives here in one place.
 *
 * `anticipation` (0..1) ramps over the final 25% of the tile's
 * approach to push glow + leading-edge brightness without flicker.
 * `dangerPulse` (0..1) sin-modulates a red overlay during the late
 * tail of the perfect-overlap window.
 *
 * `lightweightTiles` collapses each tile from "gradient body + inner
 * glow gradient + shadow blur + decorations" to "flat body fill +
 * leading-edge strip + decorations". A busy chart paints 20+ tiles
 * per frame, and on a midrange phone the gradient fills + shadowBlur
 * passes are the dominant per-frame GPU cost — the flat-fill path is
 * roughly an order of magnitude cheaper to composite.
 */

/**
 * One tap tile. High-tier rendering: rounded rect with a subtle
 * vertical gradient (top is the base colour, bottom is brighter so
 * the bottom edge reads as the "leading edge" the player aligns with
 * the hit line), a glow halo, an inner bottom highlight that follows
 * the rounded silhouette, and an anticipation boost in the last
 * quarter of the approach. Dual (chord) tiles get a paired-tie marker
 * near the top so the player reads "this is a partner tile" without
 * relying on colour discrimination. Danger pulse overlays a red
 * strobe.
 *
 * `reducedMotion` gates the inner-glow gradient + per-tile animation
 * brightening. `lightweightTiles` switches to a stripped-down,
 * gradient-free path: a flat body fill + bright leading-edge strip,
 * which preserves the "tile silhouette + glowing bottom" read at a
 * fraction of the per-frame paint cost on mobile compositors.
 */
export function drawTile(
  ctx: CanvasRenderingContext2D,
  x: number,
  bottomY: number,
  width: number,
  height: number,
  baseColour: string,
  palette: PianoChart['palette'],
  dangerPulse: number,
  isDual: boolean,
  anticipation: number,
  reducedMotion: boolean,
  lightweightTiles: boolean,
): void {
  const topY = bottomY - height;
  const r = TILE_CORNER_RADIUS_PX;

  ctx.save();
  if (lightweightTiles) {
    // Flat body fill — much cheaper than a per-frame linear gradient
    // for mobile compositors. The bright leading-edge strip below
    // does the visual work of "this edge is what aligns with the
    // hit line" that the gradient bottom-stop used to carry.
    ctx.fillStyle = baseColour;
    roundRect(ctx, x, topY, width, height, r);
    ctx.fill();
  } else {
    const gradient = ctx.createLinearGradient(0, topY, 0, bottomY);
    gradient.addColorStop(0, withAlpha(baseColour, 0.85));
    gradient.addColorStop(1, baseColour);
    ctx.shadowColor = baseColour;
    ctx.shadowBlur = 14 + anticipation * 8;
    ctx.fillStyle = gradient;
    roundRect(ctx, x, topY, width, height, r);
    ctx.fill();
    ctx.shadowBlur = 0;
  }

  // Inner leading-edge glow: bottom ~28px of the tile body brightens
  // toward white. Skipped on lightweight tier — its gradient is the
  // second-most expensive per-tile fill after the body, and the
  // leading-edge white strip below carries enough of the same cue to
  // keep the silhouette readable.
  if (!reducedMotion && !lightweightTiles && height > 32) {
    const glowH = 28;
    const glowAlpha = 0.28 + anticipation * 0.18;
    const innerGlow = ctx.createLinearGradient(0, bottomY - glowH, 0, bottomY);
    innerGlow.addColorStop(0, withAlpha('#ffffff', 0));
    innerGlow.addColorStop(1, withAlpha('#ffffff', glowAlpha));
    ctx.fillStyle = innerGlow;
    roundRect(ctx, x, bottomY - glowH, width, glowH, r);
    ctx.fill();
  }

  // Bright leading-edge strip. Doubles as the headlight "this is the
  // edge the player aligns with" cue on lightweight tier where the
  // gradient body + inner glow are absent.
  ctx.fillStyle = withAlpha('#ffffff', 0.85 + anticipation * 0.1);
  roundRect(ctx, x, bottomY - 3, width, 3, 1.5);
  ctx.fill();

  // Subtle top-edge highlight — purely decorative, skip on
  // lightweight tier where every per-tile fill counts.
  if (!lightweightTiles) {
    ctx.fillStyle = withAlpha('#ffffff', 0.12);
    roundRect(ctx, x + 4, topY + 4, width - 8, 8, 2);
    ctx.fill();
  }

  if (isDual) {
    drawDualMark(ctx, x, topY + 16, width);
  }

  if (dangerPulse > 0) {
    ctx.fillStyle = withAlpha(palette.hazard, 0.35 + dangerPulse * 0.35);
    roundRect(ctx, x, topY, width, height, r);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * Hold tile as a tall ribbon spanning from `tailY` (the scrolling
 * tail end, above) down to `headY` (the leading edge, below or
 * pinned to the hit line during the hold). While the hold is active
 * (`heldActive=true`) the body brightens and the tail grows a glow
 * cap to telegraph "let go when this reaches the hit line". Mirrors
 * {@link drawTile}'s visual language (rounded, gradient, glow halo,
 * inner leading-edge glow, dual indicator) so holds read as
 * elongated tiles, not a different object class.
 *
 * The base colour is lightened slightly vs taps (see
 * {@link HOLD_TILE_LIGHTEN_AMOUNT}) and the body is dressed with
 * upward chevrons so the player has TWO redundant "this is a hold"
 * cues: a subtle hue/value shift across the whole ribbon, plus an
 * unambiguous arrow pattern that survives even when the body is
 * partly off-screen.
 *
 * `lightweightTiles` flattens the body fill the same way as
 * {@link drawTile} — held ribbons get a slightly brighter flat tone
 * (vs the unheld base) so the "this is scoring" cue remains, but
 * the per-tile gradient and shadow passes are skipped for the
 * mobile GPU.
 */
export function drawHoldTile(
  ctx: CanvasRenderingContext2D,
  x: number,
  headY: number,
  tailY: number,
  width: number,
  baseColour: string,
  palette: PianoChart['palette'],
  dangerPulse: number,
  heldActive: boolean,
  headDone: boolean,
  isDual: boolean,
  anticipation: number,
  reducedMotion: boolean,
  lightweightTiles: boolean,
): void {
  const r = TILE_CORNER_RADIUS_PX;
  // Total visible body height — capped at headY (don't draw below
  // the hit line) and floored at 0 (defensive in case tail is somehow
  // past head).
  const bodyTop = Math.min(tailY, headY);
  const bodyBottom = Math.max(headY, bodyTop);
  const bodyHeight = bodyBottom - bodyTop;
  if (bodyHeight < 1) {
    return;
  }
  // Hold-specific tint: white-mixed so the ribbon reads brighter and
  // softer than the matching tap colour. Subtle on its own — the
  // chevron stream does the heavy lifting of distinguishing holds
  // from taps — but the combination of "lighter body + arrows" is
  // recognisable at a glance even in dim viewing conditions.
  const holdColour = lightenToward(baseColour, HOLD_TILE_LIGHTEN_AMOUNT);

  ctx.save();
  if (lightweightTiles) {
    // Flat fill: held tiles get a brighter accent so "this is scoring"
    // still reads at a glance even without the gradient + glow.
    ctx.fillStyle = heldActive ? lightenToward(holdColour, 0.4) : holdColour;
    roundRect(ctx, x, bodyTop, width, bodyHeight, r);
    ctx.fill();
  } else {
    const gradient = ctx.createLinearGradient(0, bodyTop, 0, bodyBottom);
    // Held tiles brighten significantly so the eye reads "this is
    // currently scoring." Unheld (still approaching) holds use the
    // same subtle gradient as taps.
    if (heldActive) {
      gradient.addColorStop(0, withAlpha(holdColour, 0.95));
      gradient.addColorStop(1, withAlpha('#ffffff', 0.6));
    } else {
      gradient.addColorStop(0, withAlpha(holdColour, 0.8));
      gradient.addColorStop(1, holdColour);
    }
    ctx.shadowColor = holdColour;
    const baseBlur = heldActive ? 22 : 14;
    ctx.shadowBlur = baseBlur + anticipation * 8;
    ctx.fillStyle = gradient;
    roundRect(ctx, x, bodyTop, width, bodyHeight, r);
    ctx.fill();
    ctx.shadowBlur = 0;
  }

  if (!headDone && !reducedMotion && !lightweightTiles && bodyHeight > 32) {
    // Inner leading-edge glow at the head (only while approaching —
    // once engaged, the head is pinned at the hit line and the tail
    // cap takes over as the moving visual target). Skipped on
    // lightweight tier alongside the body gradient.
    const glowH = 28;
    const glowAlpha = 0.28 + anticipation * 0.18;
    const innerGlow = ctx.createLinearGradient(0, headY - glowH, 0, headY);
    innerGlow.addColorStop(0, withAlpha('#ffffff', 0));
    innerGlow.addColorStop(1, withAlpha('#ffffff', glowAlpha));
    ctx.fillStyle = innerGlow;
    roundRect(ctx, x, headY - glowH, width, glowH, r);
    ctx.fill();
  }

  if (!headDone) {
    ctx.fillStyle = withAlpha('#ffffff', 0.85 + anticipation * 0.1);
    roundRect(ctx, x, headY - 3, width, 3, 1.5);
    ctx.fill();
  }

  if (heldActive) {
    ctx.fillStyle = withAlpha('#ffffff', 0.7);
    roundRect(ctx, x, bodyTop, width, 5, 2);
    ctx.fill();
  }

  if (!heldActive && !lightweightTiles) {
    ctx.fillStyle = withAlpha('#ffffff', 0.12);
    roundRect(ctx, x + 4, bodyTop + 4, width - 8, 8, 2);
    ctx.fill();
  }

  // Upward-chevron stream — repeated arrows climbing the ribbon so
  // the player has an immediately legible "hold this" cue even when
  // colour discrimination is degraded. Skipped on stub ribbons where
  // a single chevron would clutter more than communicate.
  if (bodyHeight >= HOLD_CHEVRON_MIN_BODY_PX) {
    drawHoldChevrons(ctx, x, bodyTop, bodyBottom, width, heldActive, reducedMotion);
  }

  // Dual (chord) indicator on holds: anchor near the head so it
  // stays on-screen even when the ribbon's tail extends well above
  // the visible region. Skip once engaged — by that point the player
  // has already committed to the hold and the marker is just clutter.
  if (isDual && !headDone) {
    const markY = headY - Math.min(bodyHeight - 8, 40);
    drawDualMark(ctx, x, markY, width);
  }

  if (dangerPulse > 0) {
    ctx.fillStyle = withAlpha(palette.hazard, 0.35 + dangerPulse * 0.35);
    roundRect(ctx, x, bodyTop, width, bodyHeight, r);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * Repeating upward chevrons painted along a hold ribbon's body. The
 * stream starts a half-spacing above the leading edge (so the first
 * arrow doesn't clip the head's white band) and climbs toward the
 * tail. The scroll-offset advances with wall-clock time regardless
 * of engagement so a tile's chevron phase is the same whether the
 * player has engaged the hold or not — engagement only changes
 * brightness, never snaps the pattern. Under reduced-motion the
 * offset is zero so the chevrons read as a static row of arrows.
 */
function drawHoldChevrons(
  ctx: CanvasRenderingContext2D,
  tileX: number,
  bodyTop: number,
  bodyBottom: number,
  tileWidth: number,
  heldActive: boolean,
  reducedMotion: boolean,
): void {
  const chevronW = Math.min(tileWidth * HOLD_CHEVRON_WIDTH_FRAC, 32);
  const chevronCentreX = tileX + tileWidth / 2;
  const chevronAlpha = heldActive ? 0.9 : 0.55;
  ctx.strokeStyle = withAlpha('#ffffff', chevronAlpha);
  ctx.lineWidth = HOLD_CHEVRON_THICKNESS_PX;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // Scroll offset wraps inside one spacing period: y-positions
  // decrease over time so each arrow visibly climbs the ribbon, and
  // when an arrow passes the wrap boundary a fresh one enters from
  // below at exactly the position the previous one occupied a moment
  // earlier — the pattern reads as a continuous upward stream rather
  // than a stepwise blink.
  const scrollOffset = reducedMotion
    ? 0
    : ((performance.now() * HOLD_CHEVRON_SCROLL_PX_PER_SEC) / 1000) % HOLD_CHEVRON_SPACING_PX;
  // March from just above the leading edge upward. The 0.5-spacing
  // offset keeps the first chevron clear of the head highlight band.
  const firstY = bodyBottom - HOLD_CHEVRON_SPACING_PX * 0.5 - scrollOffset;
  const stopY = bodyTop + HOLD_CHEVRON_HEIGHT_PX + 4;
  for (let y = firstY; y >= stopY; y -= HOLD_CHEVRON_SPACING_PX) {
    ctx.beginPath();
    ctx.moveTo(chevronCentreX - chevronW / 2, y);
    ctx.lineTo(chevronCentreX, y - HOLD_CHEVRON_HEIGHT_PX);
    ctx.lineTo(chevronCentreX + chevronW / 2, y);
    ctx.stroke();
  }
}

/**
 * Twin parallel stripes drawn near the top of a dual (chord) tile.
 * Reads as a musical tie / linked-pair mark so the player instantly
 * identifies "this tile is one half of a chord" without relying on
 * colour discrimination (chord-tile colour-coding can be ambiguous
 * for colour-blind players or in dim viewing conditions).
 */
function drawDualMark(
  ctx: CanvasRenderingContext2D,
  tileX: number,
  centreY: number,
  tileWidth: number,
): void {
  const stripeW = Math.min(28, tileWidth * 0.32);
  const stripeX = tileX + (tileWidth - stripeW) / 2;
  ctx.fillStyle = withAlpha('#ffffff', 0.6);
  ctx.fillRect(stripeX, centreY - 3, stripeW, 2);
  ctx.fillRect(stripeX, centreY + 2, stripeW, 2);
}
